import { getApiBase, userApiUrl } from "./apiConfig";
import { adminAuthedFetch } from "./adminAuthedFetch";
import {
  ADMIN_AUTH_FLAG_KEY,
  clearAdminJwt,
  getAdminJwt,
  setAdminJwt,
} from "./authToken";

export class AuthApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
    this.body = body;
  }
}

async function parseJson(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function loginErrorMessage(status, data) {
  const m = data?.message || data?.error;
  if (typeof m === "string" && m.trim()) return m;
  if (status === 401) return "Invalid email or password.";
  if (status === 423) return "Account locked. Try again later.";
  if (status === 429) return "Too many attempts. Wait before trying again.";
  return `Sign in failed (${status})`;
}

export async function loginAdmin(email, password) {
  const base = getApiBase();
  if (!base) {
    return { mock: true, token: null, user: null };
  }

  const url = userApiUrl("Login");
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  const data = await parseJson(res);
  if (!res.ok) {
    throw new AuthApiError(loginErrorMessage(res.status, data), { status: res.status, body: data });
  }

  if (data.success === false) {
    throw new AuthApiError(data.message || "Sign in failed", { status: res.status, body: data });
  }

  const token = data.token;
  if (!token || typeof token !== "string") {
    throw new AuthApiError("No token returned from server", { status: res.status, body: data });
  }

  setAdminJwt(token);
  return { mock: false, token, user: data.user ?? null };
}

export async function logoutAdmin() {
  const url = userApiUrl("Logout");
  if (getApiBase() && url) {
    try {
      await fetch(url, { method: "POST" });
    } catch {
      // network error — still clear client session
    }
  }
  clearAdminJwt();
  try {
    localStorage.removeItem(ADMIN_AUTH_FLAG_KEY);
  } catch {
    // ignore
  }
}

export async function forgotPassword(email) {
  const url = userApiUrl("forgot-password");
  if (!getApiBase() || !url) {
    throw new AuthApiError("API not configured", { status: 0 });
  }
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new AuthApiError(
      typeof data.message === "string" ? data.message : "Request failed",
      { status: res.status, body: data }
    );
  }
}

export async function resetPassword({ token, newPassword }) {
  const url = userApiUrl("reset-password");
  if (!getApiBase() || !url) {
    throw new AuthApiError("API not configured", { status: 0 });
  }
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword }),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new AuthApiError(
      typeof data.message === "string" ? data.message : "Could not reset password",
      { status: res.status, body: data }
    );
  }
}

export async function fetchUserProfile() {
  const url = userApiUrl("profile");
  const jwt = getAdminJwt();
  if (!getApiBase() || !url) {
    throw new AuthApiError("API not configured", { status: 0 });
  }
  if (!jwt) {
    throw new AuthApiError("Authentication required", { status: 401 });
  }
  const res = await adminAuthedFetch(url, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  const data = await parseJson(res);
  if (!res.ok) {
    const msg =
      typeof data.message === "string"
        ? data.message
        : res.status === 401
          ? "Session expired"
          : "Could not load profile";
    throw new AuthApiError(msg, { status: res.status, body: data });
  }
  return data.user ?? data;
}

export async function changePassword({ currentPassword, newPassword }) {
  const url = userApiUrl("change-password");
  const jwt = getAdminJwt();
  if (!getApiBase() || !url) {
    throw new AuthApiError("API not configured", { status: 0 });
  }
  if (!jwt) {
    throw new AuthApiError("Authentication required", { status: 401 });
  }
  const res = await adminAuthedFetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new AuthApiError(
      typeof data.message === "string" ? data.message : "Could not change password",
      { status: res.status, body: data }
    );
  }
}
